import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import history from '../../history';
import swal from 'sweetalert';
import { Form, Input, Button } from 'semantic-ui-react';
import { SUPPORTED_CURRENCIES } from '../../utils/currency';
import { showPackage, updatePackage, createPackageJson } from '../../api/packageApi';

// Private tours: vendor_total_price = total for the trip
const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  vendor_total_price: yup.number().required('Required').min(0.01, 'Must be > 0'),
  currency_code: yup.string().required('Currency is required').oneOf(['NPR', 'USD'], 'Must be NPR or USD'),
  markup_percentage: yup.number().nullable().min(0).max(100),
  interest_percentage: yup.number().nullable().min(0).max(100)
});

class PrivateTourForm extends Component {
  state = { tour: null, loading: true, fetchError: null };

  getBackTo() {
    return this.props.backTo || '/admin/private_tours';
  }

  getSuccessTo() {
    return this.props.successTo || '/admin/private_tours';
  }

  componentDidMount() {
    const idx = this.props.match && this.props.match.params && this.props.match.params.idx;
    const isCreate = !idx || idx === 'new';
    const fromState = this.props.location && this.props.location.state && this.props.location.state.privateTour;

    // If we have state data (from navigation), use it
    if (fromState && (fromState.idx || fromState.id)) {
      this.setState({ tour: fromState, loading: false, fetchError: null });
    } else if (idx && !isCreate) {
      // Otherwise fetch from API using URL param
      showPackage(idx)
        .then((r) => {
          let data = r.data;
          if (data && !data.idx && data.package) {
            data = data.package;
          }
          if (!data || (!data.name && !data.idx)) {
            this.setState({ loading: false, fetchError: 'Package not found or empty response' });
            return;
          }
          this.setState({ tour: data, loading: false, fetchError: null });
        })
        .catch((err) => {
          this.setState({ loading: false, fetchError: 'Failed to load private tour: ' + (err.response?.data?.errors?.join(' ') || err.message) });
        });
    } else {
      this.setState({ loading: !isCreate });
    }
  }

  getInitial() {
    const raw = this.state.tour || {};
    const routeState = this.props.location && this.props.location.state;
    // When opening Edit from inquiry details, use the inquiry's total amount so the form shows the same price (e.g. 4800)
    if (routeState && routeState.fromInquiry && routeState.totalAmount != null && routeState.totalAmount !== '') {
      const priceVal = Number(routeState.totalAmount);
      const g = raw && raw.package && (raw.idx == null && raw.price == null) ? raw.package : raw;
      return {
        name: g.name || '',
        description: g.description || '',
        destination: g.destination || '',
        origin: g.origin || '',
        vendor_total_price: Number.isFinite(priceVal) ? priceVal : '',
        markup_percentage: g.markup_percentage != null && g.markup_percentage !== '' ? g.markup_percentage : '',
        interest_percentage: g.interest_percentage != null && g.interest_percentage !== '' ? g.interest_percentage : '',
        published: !!g.published
      };
    }
    const g = raw && raw.package && (raw.idx == null && raw.price == null) ? raw.package : raw;
    const priceVal = (g.vendor_total_price != null && g.vendor_total_price !== '') ? Number(g.vendor_total_price) : (g.price != null && g.price !== '' ? Number(g.price) : '');
    const cc = g.currency_code || (g.vendor_total_price_money && g.vendor_total_price_money.currency_code) || 'NPR';
    return {
      name: g.name || '',
      description: g.description || '',
      destination: g.destination || '',
      origin: g.origin || '',
      vendor_total_price: priceVal,
      currency_code: cc,
      markup_percentage: g.markup_percentage != null && g.markup_percentage !== '' ? g.markup_percentage : '',
      interest_percentage: g.interest_percentage != null && g.interest_percentage !== '' ? g.interest_percentage : '',
      published: !!g.published
    };
  }

  isVendor() {
    return (this.getBackTo() || '').includes('partner');
  }

  render() {
    const { tour, loading, fetchError } = this.state;
    const idx = this.props.match && this.props.match.params && this.props.match.params.idx;
    const isEdit = !!(idx && idx !== 'new') || (tour && tour.idx); // Edit mode if we have idx in URL (not 'new') OR tour data with idx

    // In edit mode: wait for tour to load
    if (isEdit && loading) {
      return <div className="container p-4">Loading…</div>;
    }

    // Show error if fetch failed
    if (isEdit && fetchError) {
      return (
        <div className="container">
          <Link to={this.getBackTo()} className="btn btn-outline-secondary btn-sm mb-3">← Back to Private Tours</Link>
          <div className="alert alert-danger">{fetchError}</div>
        </div>
      );
    }

    // Show error if package not found (edit mode but no data after loading)
    if (isEdit && !tour) {
      return (
        <div className="container">
          <Link to={this.getBackTo()} className="btn btn-outline-secondary btn-sm mb-3">← Back to Private Tours</Link>
          <div className="alert alert-danger">Private tour not found.</div>
        </div>
      );
    }

    return (
      <div className="container">
        <Link to={this.getBackTo()} className="btn btn-outline-secondary btn-sm mb-3">← Back to Private Tours</Link>
        <div className="card">
          <div className="card-body">
            <h3 className="title">{isEdit ? 'Edit Private Tour' : 'New Private Tour'}</h3>
            {isEdit && tour && tour.has_any_inquiry_or_booking && (
              <div className="alert alert-warning mb-3">
                <strong>Editing is locked.</strong> This private tour has inquiries or bookings. You cannot change details.
              </div>
            )}
            <p className="text-muted small mb-3">
              Customer submits inquiry with their preferred date and number of travellers.
            </p>
            <Formik
              key={tour ? tour.idx : 'new'} // Force remount when tour data changes
              initialValues={this.getInitial()}
              validationSchema={schema}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                const tourIdx = (tour && tour.idx) || idx; // Use tour.idx if available, fallback to URL idx
                const successTo = this.getSuccessTo();
                const major = parseFloat(values.vendor_total_price);
                const cc = (values.currency_code || 'NPR').toUpperCase();
                const payload = {
                  name: values.name,
                  description: values.description || '',
                  origin: values.origin || '',
                  destination: values.destination || '',
                  price: Number(values.vendor_total_price),
                  vendor_total_price: Number(values.vendor_total_price),
                  vendor_total_price_minor: Number.isFinite(major) ? Math.round(major * 100) : undefined,
                  currency_code: cc,
                  published: values.published
                };
                if (!this.isVendor()) {
                  payload.markup_percentage = (values.markup_percentage !== '' && values.markup_percentage != null) ? Number(values.markup_percentage) : null;
                  payload.interest_percentage = (values.interest_percentage !== '' && values.interest_percentage != null) ? Number(values.interest_percentage) : null;
                }
                if (isEdit) {
                  payload.addons = (tour && (tour.addons || []))
                    ? (tour.addons || []).map((a) => (typeof a === 'object' ? a.id : a)).filter(Boolean)
                    : [];
                  updatePackage(tourIdx, payload)
                    .then(() => { swal('Updated', { icon: 'success' }); history.push(successTo); })
                    .catch((e) => { swal('Error', (e.response && e.response.data && e.response.data.errors && e.response.data.errors.join(' ')) || 'Update failed', { icon: 'error' }); })
                    .finally(() => setSubmitting(false));
                } else {
                  createPackageJson(payload)
                    .then(() => { swal('Created', { icon: 'success' }); history.push(successTo); })
                    .catch((e) => { swal('Error', (e.response && e.response.data && e.response.data.errors && e.response.data.errors.join(' ')) || 'Create failed', { icon: 'error' }); })
                    .finally(() => setSubmitting(false));
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => {
                const formLocked = isEdit && tour && tour.has_any_inquiry_or_booking;
                return (
                <Form onSubmit={handleSubmit}>
                  <Form.Field error={touched.name && errors.name}>
                    <label>Name *</label>
                    <Input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} disabled={formLocked} />
                    {touched.name && errors.name && <span className="text-danger small">{errors.name}</span>}
                  </Form.Field>
                  <Form.Field>
                    <label>Description</label>
                    <Form.TextArea name="description" value={values.description} onChange={(e, { value }) => setFieldValue('description', value)} onBlur={handleBlur} rows={3} disabled={formLocked} />
                  </Form.Field>
                  <Form.Field>
                    <label>From (origin)</label>
                    <Input name="origin" value={values.origin} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Kathmandu" disabled={formLocked} />
                  </Form.Field>
                  <Form.Field>
                    <label>To (destination)</label>
                    <Input name="destination" value={values.destination} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Annapurna Base Camp" disabled={formLocked} />
                  </Form.Field>
                  <Form.Field error={touched.currency_code && errors.currency_code}>
                    <label>Currency *</label>
                    <Form.Select
                      options={SUPPORTED_CURRENCIES.map(({ code, label }) => ({ key: code, value: code, text: label }))}
                      value={values.currency_code || 'NPR'}
                      onChange={(e, { value }) => setFieldValue('currency_code', value)}
                      disabled={formLocked}
                      placeholder="Select currency"
                    />
                    {formLocked && <span className="text-muted small d-block mt-1">Form is read-only while inquiries or bookings exist.</span>}
                    {touched.currency_code && errors.currency_code && <span className="text-danger small">{errors.currency_code}</span>}
                  </Form.Field>
                  <Form.Field error={touched.vendor_total_price && errors.vendor_total_price}>
                    <label>Total price you are asking (total for the trip) *</label>
                    <Input type="number" min={0.01} step="0.01" name="vendor_total_price" value={values.vendor_total_price || ''} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. 4800" disabled={formLocked} />
                    {touched.vendor_total_price && errors.vendor_total_price && <span className="text-danger small">{errors.vendor_total_price}</span>}
                    <span className="text-muted small d-block mt-1">{values.currency_code === 'USD' ? 'USD' : 'NPR'} total for the whole trip (not per person).</span>
                  </Form.Field>
                  {!this.isVendor() && (
                    <>
                      <Form.Field error={touched.markup_percentage && errors.markup_percentage}>
                        <label>Markup % (admin)</label>
                        <Input type="number" min={0} step="0.01" name="markup_percentage" value={values.markup_percentage || ''} onChange={handleChange} onBlur={handleBlur} placeholder="Blank = platform default" disabled={formLocked} />
                      </Form.Field>
                      <Form.Field error={touched.interest_percentage && errors.interest_percentage}>
                        <label>Interest % (admin)</label>
                        <Input type="number" min={0} max={100} step="0.01" name="interest_percentage" value={values.interest_percentage || ''} onChange={handleChange} onBlur={handleBlur} placeholder="Blank = platform default" disabled={formLocked} />
                      </Form.Field>
                    </>
                  )}
                  <Button type="submit" primary disabled={isSubmitting || formLocked}>{isEdit ? 'Update' : 'Create'}</Button>
                  <Button type="button" onClick={() => history.push(this.getBackTo())} className="ml-2">Cancel</Button>
                </Form>
              );
              }}
            </Formik>
          </div>
        </div>
      </div>
    );
  }
}

export default PrivateTourForm;
